/**
 * Memoized navigation selectors.
 *
 * ForYou nav children are derived data — a pure function of
 * (preferences, navTree, auth status).  Using createSelector
 * keeps this computation out of reducers and components, and
 * ensures it only recalculates when the inputs actually change.
 */
import { createSelector } from '@reduxjs/toolkit';

// ── Raw input selectors ──────────────────────────────────────
const selectRawNavItems = (state) => state.headerSlice?.navigationItems || [];
const selectFollowedCategories = (state) => state.forYou?.followedCategories || [];
const selectFollowedTopics = (state) => state.forYou?.followedTopics || [];
const selectHasPreferences = (state) => state.forYou?.hasPreferences || false;
const selectIsAuthenticated = (state) => state.auth?.isAuthenticated || false;

// IDs of synthetic nav items that should not be treated as categories
const SYNTHETIC_IDS = new Set(['home', 'for-you', 'admin', 'about']);

/**
 * Build ForYou nav children from user preferences + the navigation tree.
 * Mirrors getForYouNavigationService logic on the server side.
 */
function buildForYouChildren(navItems, followedCategories, followedTopics) {
    const catSet = new Set(followedCategories);
    const topicSet = new Set(followedTopics);

    if (catSet.size === 0 && topicSet.size === 0) return [];

    const children = [];
    const addedTopicNames = new Set();

    // Pass 1: collect followed categories and their followed topics
    navItems.forEach((item) => {
        if (SYNTHETIC_IDS.has(item.id)) return;
        if (!catSet.has(item.name)) return;

        const catChild = {
            id: `fy-${item.id}`,
            name: item.name,
            label: item.label,
            link: item.link,
            children: [],
        };

        const collectTopics = (itemChildren) => {
            (itemChildren || []).forEach((child) => {
                if (topicSet.has(child.name)) {
                    catChild.children.push({
                        id: `fy-${child.id}`,
                        name: child.name,
                        label: child.label,
                        link: child.link,
                        children: [],
                    });
                    addedTopicNames.add(child.name);
                }
                if (child.children?.length) collectTopics(child.children);
            });
        };
        collectTopics(item.children);
        children.push(catChild);
    });

    // Pass 2: orphaned topics (parent category not followed)
    navItems.forEach((item) => {
        if (SYNTHETIC_IDS.has(item.id) || catSet.has(item.name)) return;

        const collectOrphans = (itemChildren) => {
            (itemChildren || []).forEach((child) => {
                if (topicSet.has(child.name) && !addedTopicNames.has(child.name)) {
                    children.push({
                        id: `fy-${child.id}`,
                        name: child.name,
                        label: child.label,
                        link: child.link,
                        children: [],
                    });
                    addedTopicNames.add(child.name);
                }
                if (child.children?.length) collectOrphans(child.children);
            });
        };
        collectOrphans(item.children);
    });

    return children;
}

/**
 * Primary selector: returns navigationItems with ForYou children
 * derived from the user's preferences.
 *
 * Memoized — only recalculates when inputs change.
 */
export const selectNavigationWithForYou = createSelector(
    [
        selectRawNavItems,
        selectFollowedCategories,
        selectFollowedTopics,
        selectHasPreferences,
        selectIsAuthenticated,
    ],
    (navItems, followedCategories, followedTopics, hasPreferences, isAuthenticated) => {
        // Find the ForYou nav item index
        const forYouIdx = navItems.findIndex((item) => item.isForYou);
        if (forYouIdx === -1) return navItems;

        // If logged out or no preferences, strip children
        if (!isAuthenticated || !hasPreferences) {
            const currentForYou = navItems[forYouIdx];
            if (!currentForYou.children || currentForYou.children.length === 0) {
                return navItems; // Already empty — return same reference for memoization
            }
            return navItems.map((item) =>
                item.isForYou ? { ...item, children: [] } : item
            );
        }

        // Build enriched children
        const forYouChildren = buildForYouChildren(navItems, followedCategories, followedTopics);
        return navItems.map((item) =>
            item.isForYou ? { ...item, children: forYouChildren } : item
        );
    }
);
